import { httpClient } from '../http-client';
import { extractApiData } from './response-utils';
import type {
  BulkInviteRequest,
  BulkInviteResponse,
  ReferralAnalyticsResponse,
  ReferralCodeResponse,
  ReferralConfigResponse,
  ReferralEarning,
  ReferralEarningsListResponse,
  ReferralEarningsSummaryResponse,
  ReferralHistoryResponse,
  ReferralInvite,
  ReferralLeaderboardResponse,
  ReferralLevelsResponse,
  ReferralNotificationsResponse,
  ReferralPayoutRequest,
  ReferralPayoutResponse,
  ReferralSettings,
  ReferralSettingsUpdateRequest,
  ReferralStatsResponse,
  ReferralUser,
  ReferralWithdrawRequest,
  ReferralWithdrawResponse,
} from '../../types/api/referrals';

export const referralsApi = {
  // Profile & code
  getProfile: async (userId: string): Promise<ReferralUser> => {
    const response = await httpClient.get<ReferralUser>('/referrals/profile', { params: { userId } });
    return extractApiData(response);
  },

  createProfile: async (userId: string): Promise<ReferralUser> => {
    const response = await httpClient.post<ReferralUser>('/referrals/profile', { userId });
    return extractApiData(response);
  },

  getCode: async (userId: string): Promise<ReferralCodeResponse> => {
    const response = await httpClient.get<ReferralCodeResponse>('/referrals/code', { params: { userId } });
    return extractApiData(response);
  },

  // Stats & config
  getStats: async (userId: string): Promise<ReferralStatsResponse> => {
    const response = await httpClient.get<ReferralStatsResponse>('/referrals/stats', { params: { userId } });
    return extractApiData(response);
  },

  getConfig: async (): Promise<ReferralConfigResponse> => {
    const response = await httpClient.get<ReferralConfigResponse>('/referrals/config');
    return extractApiData(response);
  },

  getLevels: async (userId?: string): Promise<ReferralLevelsResponse> => {
    const response = await httpClient.get<ReferralLevelsResponse>('/referrals/levels', {
      params: userId ? { userId } : undefined,
    });
    return extractApiData(response);
  },

  // Earnings
  getEarnings: async (params: {
    userId: string;
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<ReferralEarningsListResponse> => {
    const response = await httpClient.get<ReferralEarningsListResponse>('/referrals/earnings', { params });
    return extractApiData(response);
  },

  getEarningsSummary: async (params: {
    userId: string;
    period?: string;
    page?: number;
    limit?: number;
  }): Promise<ReferralEarningsSummaryResponse> => {
    const { userId, period } = params;
    const response = await httpClient.get<ReferralEarningsSummaryResponse>('/referrals/earnings/summary', {
      params: { userId, ...(period ? { period } : {}) },
    });
    // Бекенд возвращает тело { data: [...], summary: {...}, pagination: {...} } напрямую.
    // httpClient.get<T> возвращает это тело as-is (не двойная обёртка),
    // поэтому response уже является ReferralEarningsSummaryResponse — не извлекаем .data.
    return response as unknown as ReferralEarningsSummaryResponse;
  },

  getAllEarnings: async (params: { status?: string; page?: number; limit?: number }) => {
    const response = await httpClient.get<any>('/referrals/earnings/admin', { params });
    return extractApiData(response);
  },

  createEarning: async (payload: Partial<ReferralEarning>): Promise<ReferralEarning> => {
    const response = await httpClient.post<ReferralEarning>('/referrals/earnings', payload);
    return extractApiData(response);
  },

  approveEarning: async (id: string): Promise<ReferralEarning> => {
    const response = await httpClient.put<ReferralEarning>(`/referrals/earnings/${id}/approve`);
    return extractApiData(response);
  },

  rejectEarning: async (id: string, reason: string): Promise<ReferralEarning> => {
    const response = await httpClient.put<ReferralEarning>(`/referrals/earnings/${id}/reject`, { reason });
    return extractApiData(response);
  },

  payEarning: async (id: string): Promise<ReferralEarning> => {
    const response = await httpClient.put<ReferralEarning>(`/referrals/earnings/${id}/pay`);
    return extractApiData(response);
  },

  // Invites
  getInvites: async (userId: string, status?: string): Promise<ReferralInvite[]> => {
    const response = await httpClient.get<ReferralInvite[]>('/referrals/invites', { params: { userId, status } });
    return extractApiData(response);
  },

  createInvite: async (payload: { userId: string; email: string }): Promise<ReferralInvite> => {
    const response = await httpClient.post<ReferralInvite>('/referrals/invites', payload);
    return extractApiData(response);
  },

  acceptInvite: async (id: string): Promise<{ success: boolean }> => {
    const response = await httpClient.post<{ success: boolean }>(`/referrals/invites/${id}/accept`);
    return extractApiData(response);
  },

  rejectInvite: async (id: string, reason: string): Promise<{ success: boolean }> => {
    const response = await httpClient.post<{ success: boolean }>(`/referrals/invites/${id}/reject`, { reason });
    return extractApiData(response);
  },

  sendBulkInvites: async (payload: BulkInviteRequest): Promise<BulkInviteResponse> => {
    const response = await httpClient.post<BulkInviteResponse>('/referrals/invite', payload);
    return extractApiData(response);
  },

  // Referrals & history
  getReferrals: async (userId: string, level?: number): Promise<ReferralUser[]> => {
    const response = await httpClient.get<ReferralUser[]>('/referrals/referrals', {
      params: { userId, level },
    });
    return extractApiData(response);
  },

  getHistory: async (params: {
    userId: string;
    page?: number;
    limit?: number;
    type?: string;
  }): Promise<ReferralHistoryResponse> => {
    const response = await httpClient.get<ReferralHistoryResponse>('/referrals/history', { params });
    return extractApiData(response);
  },

  // Analytics
  getAnalytics: async (userId: string): Promise<ReferralAnalyticsResponse> => {
    const response = await httpClient.get<ReferralAnalyticsResponse>('/referrals/analytics', { params: { userId } });
    return extractApiData(response);
  },

  getLeaderboard: async (): Promise<ReferralLeaderboardResponse> => {
    const response = await httpClient.get<ReferralLeaderboardResponse>('/referrals/analytics/leaderboard');
    return extractApiData(response);
  },

  getUCBreakdown: async (): Promise<{ total: number; expiringSoon: number; byExpiration: Array<{ bucket: string; amount: number }> }> => {
    const response = await httpClient.get<{ total: number; expiringSoon: number; byExpiration: Array<{ bucket: string; amount: number }> }>('/referrals/uc/breakdown');
    return extractApiData(response);
  },

  // Payouts & withdrawal
  requestPayout: async (payload: ReferralPayoutRequest): Promise<ReferralPayoutResponse> => {
    const response = await httpClient.post<ReferralPayoutResponse>('/referrals/payouts/request', payload);
    return extractApiData(response);
  },

  getPayouts: async (userId: string): Promise<any> => {
    const response = await httpClient.get<any>('/referrals/payouts', { params: { userId } });
    return extractApiData(response);
  },

  withdraw: async (payload: ReferralWithdrawRequest): Promise<ReferralWithdrawResponse> => {
    const response = await httpClient.post<ReferralWithdrawResponse>('/referrals/withdraw', payload);
    return extractApiData(response);
  },

  // Settings & notifications
  getSettings: async (userId: string): Promise<ReferralSettings> => {
    const response = await httpClient.get<ReferralSettings>('/referrals/settings', { params: { userId } });
    return extractApiData(response);
  },

  updateSettings: async (
    userId: string,
    payload: ReferralSettingsUpdateRequest
  ): Promise<ReferralSettings> => {
    const response = await httpClient.put<ReferralSettings>('/referrals/settings', payload, { params: { userId } });
    return extractApiData(response);
  },

  getNotifications: async (userId: string): Promise<ReferralNotificationsResponse> => {
    const response = await httpClient.get<ReferralNotificationsResponse>('/referrals/notifications', { params: { userId } });
    return extractApiData(response);
  },
};
